import Units from "../../core/unitConverter";
import InvalidArgumentError from "../../Errors/InvalidArgumentError";
import BodyMassIndex from "../../HealthCalculators/BodyMassIndex";
import BodyMassIndexGuideline from "../BodyMassIndex/SiSU";

export default class SiSU {
    /**
     * Constructor
     *
     * @param {number} weight in kg
     * @param {number} height in cm
     * @param {string|null} ethnicity - resolved NHS ethnicity value (e.g. "Caribbean") or a
     *   fallback question code. For EthnicityBmi populations the band boundaries shift down
     *   (NICE 1.9.11), mirroring the BMI guideline — the weight bands ARE BMI thresholds
     *   converted to weight, so they must move together.
     */
    constructor(weight, height, ethnicity = null) {
        this._weight = weight;
        this._height = height;
        this._ethnicity = ethnicity;
        // Single source of truth for the band cutoffs — shared with the BMI guideline.
        this._thresholds = BodyMassIndexGuideline.thresholdsForEthnicity(ethnicity);
        this._isMetric = true;
        this._validateWeightValue(this._weight);
        this._validateHeightValue(this._height);
    }

    /**
     * Returns the given Weight value
     * @returns {Number}
     */
    get() {
        return this._weight;
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get RiskName() {
        return "WEIGHT";
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get GuidelineName() {
        return "SISU_WEIGHT";
    }

    /**
     * Validates the given Weight value. It is intended to be a positive number
     *
     * @returns {boolean}
     * @private
     */
    _validateWeightValue(weight) {
        if (!(typeof weight === "number") || Number.isNaN(weight) || weight < 0) {
            throw new InvalidArgumentError("Weight must be a valid number greater than or equal to 0");
        }

        return true;
    }

    /**
     * Validates the given Height value. It is intended to be a positive number
     *
     * @returns {boolean}
     * @private
     */
    _validateHeightValue(height) {
        if (!(typeof height === "number") || Number.isNaN(height) || height < 0) {
            throw new InvalidArgumentError("Height must be a valid number greater than or equal to 0");
        }

        return true;
    }

    /**
     * Returns weight converted to Imperial value (in lbs)
     *
     * @returns {number}
     */
    setUnitToImperial() {
        if (!this._isMetric) return this._weight;
        this._isMetric = false;
        this._weight = this._convertWeightWRTUnit(this._weight);
        return this._weight;
    }

    /**
     * Returns weight converted to metric value
     *
     * @returns {number}
     */
    setUnitToMetric() {
        if (this._isMetric) return this._weight;
        this._isMetric = true;
        this._weight = this._convertWeightWRTUnit(this._weight);
        return this._weight;
    }

    /**
     * The healthy weight band for this height and ethnicity — the NICE normal BMI band
     * converted to weight. Returned in the instance's current unit.
     *
     * @returns {{lower: number, upper: number}}
     */
    get targetWeightRange() {
        const { lower, upper } = BodyMassIndexGuideline.healthyBmiRangeForEthnicity(this._ethnicity);
        const toCurrentUnit = bmi => {
            const weightInKg = BodyMassIndex.getWeightForHeightAndBMI(this._height, bmi);
            return this._isMetric ? weightInKg : this._convertWeightWRTUnit(weightInKg);
        };

        return { lower: toCurrentUnit(lower), upper: toCurrentUnit(upper) };
    }

    /**
     * Returns weight converted to metric or imperial (In lbs)
     *
     * @param {number} wt weight to convert
     * @returns {number}
     * @private
     */
    _convertWeightWRTUnit(wt) {
        const weight = this._isMetric ? Units.poundsToKgs(wt) : Units.kgToPounds(wt);
        return weight;
    }

    /**
     * Returns weight converted to metric or imperial (In lbs)
     *
     * @param {number} bmi body mass index
     * @param {boolean} round a flag to get a rounded weight value for bmi
     * @returns {number}
     */
    _calculateWeightWRTBmi(bmi, round = false) {
        let weight = BodyMassIndex.getWeightForHeightAndBMI(this._height, bmi);
        weight = this._isMetric ? weight : Units.kgToPounds(weight);
        return round ? Math.round(weight) : weight;
    }

    /**
     * Returns an object representing various data for each Weight range
     *
     * @returns {object}
     */
    get ranges() {
        const t = this._thresholds;
        // Display upper-bound of a band is the next band's start minus 0.1 BMI
        // (e.g. standard normal shows "… - weight(24.9)" where the next band starts at 25).
        const displayTop = SiSU._BMI_DISPLAY_GAP;

        return {
            low: {
                key: "low",
                label: "Low",
                range: `< ${this._calculateWeightWRTBmi(t.low, true)}`
            },

            normal: {
                key: "normal",
                label: "Normal",
                range: `${this._calculateWeightWRTBmi(t.low, true)} - ${this._calculateWeightWRTBmi(
                    t.normal - displayTop,
                    true
                )}`
            },

            elevated: {
                key: "elevated",
                label: "Elevated",
                range: `${this._calculateWeightWRTBmi(t.normal, true)} - ${this._calculateWeightWRTBmi(
                    t.elevated - displayTop,
                    true
                )}`
            },

            high: {
                key: "high",
                label: "High",
                range: `${this._calculateWeightWRTBmi(t.elevated, true)} - ${this._calculateWeightWRTBmi(
                    t.high - displayTop,
                    true
                )}`
            },

            veryHigh: {
                key: "veryHigh",
                label: "Very High",
                range: `${this._calculateWeightWRTBmi(t.high, true)}+`
            }
        };
    }

    // The gap between a band's inclusive display top and the next band's start (BMI units).
    static get _BMI_DISPLAY_GAP() {
        return 0.1;
    }

    // NHS reports BMI to one decimal place (half-up). Classifying on the 1dp BMI means
    // `round1dp(bmi) >= T` which is equivalent to `bmi >= T - 0.05`, i.e. comparing the
    // weight against the weight at (T - 0.05). This keeps the Weight summary band in step
    // with the BMI guideline (and the BMI result screen) for borderline values like 24.95.
    static get _BMI_ROUNDING_OFFSET() {
        return 0.05;
    }

    _weightAtBmiBoundary(bmiThreshold) {
        return this._calculateWeightWRTBmi(bmiThreshold - SiSU._BMI_ROUNDING_OFFSET);
    }

    /**
     * Returns if the given Weight is in the 'low' range
     *
     * @returns {boolean}
     */
    isLow() {
        return this._weight < this._weightAtBmiBoundary(this._thresholds.low);
    }

    /**
     * Returns if the given Weight is in the 'normal' range
     *
     * @returns {boolean}
     */
    isNormal() {
        return (
            this._weight >= this._weightAtBmiBoundary(this._thresholds.low) &&
            this._weight < this._weightAtBmiBoundary(this._thresholds.normal)
        );
    }

    /**
     * Returns if the given Weight is in the 'elevated' range
     *
     * @returns {boolean}
     */
    isElevated() {
        return (
            this._weight >= this._weightAtBmiBoundary(this._thresholds.normal) &&
            this._weight < this._weightAtBmiBoundary(this._thresholds.elevated)
        );
    }

    /**
     * Returns if the given Weight is in the 'high' range
     *
     * @returns {boolean}
     */
    isHigh() {
        return (
            this._weight >= this._weightAtBmiBoundary(this._thresholds.elevated) &&
            this._weight < this._weightAtBmiBoundary(this._thresholds.high)
        );
    }

    /**
     * Returns if the given Weight is in the 'very high' range
     *
     * @returns {boolean}
     */
    isVeryHigh() {
        return this._weight >= this._weightAtBmiBoundary(this._thresholds.high);
    }

    /**
     * returns help information about the calculated range
     *
     * @returns {string}
     */
    get rangeInfo() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = this.ranges.low.range;
                break;
            case this.isNormal():
                info = this.ranges.normal.range;
                break;
            case this.isElevated():
                info = this.ranges.elevated.range;
                break;
            case this.isHigh():
                info = this.ranges.high.range;
                break;
            case this.isVeryHigh():
                info = this.ranges.veryHigh.range;
                break;
        }

        return info;
    }

    /**
     * returns key about the calculated range
     *
     * @returns {string}
     */
    get key() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = this.ranges.low.key;
                break;
            case this.isNormal():
                info = this.ranges.normal.key;
                break;
            case this.isElevated():
                info = this.ranges.elevated.key;
                break;
            case this.isHigh():
                info = this.ranges.high.key;
                break;
            case this.isVeryHigh():
                info = this.ranges.veryHigh.key;
                break;
        }

        return info;
    }

    /**
     * Returns label for the calculated range
     *
     * @returns {string}
     */
    get label() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = this.ranges.low.label;
                break;
            case this.isNormal():
                info = this.ranges.normal.label;
                break;
            case this.isElevated():
                info = this.ranges.elevated.label;
                break;
            case this.isHigh():
                info = this.ranges.high.label;
                break;
            case this.isVeryHigh():
                info = this.ranges.veryHigh.label;
                break;
        }

        return info;
    }

    /**
     * Calculates a risk level number between 0 - 1 for each rating
     *
     * @return {number}
     */
    get riskLevel() {
        let totalRiskLevel = 5,
            riskLevel = 0;
        switch (true) {
            case this.isLow():
                riskLevel = 1 / (totalRiskLevel + 1);
                break;
            case this.isNormal():
                riskLevel = 2 / (totalRiskLevel + 1);
                break;
            case this.isElevated():
                riskLevel = 3 / (totalRiskLevel + 1);
                break;
            case this.isHigh():
                riskLevel = 4 / (totalRiskLevel + 1);
                break;
            case this.isVeryHigh():
                riskLevel = 5 / (totalRiskLevel + 1);
                break;
        }

        return riskLevel;
    }
}
